import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material'
import CopyIcon from '../../common/CopyIcon'
import { archivalApi, errorText } from './archivalUtils'

/** Presigned download links for every S3 part of one run (GET /runs/{id}/links). Links expire — fetched on open. */
export default function S3LinksDialog({ open, run, onClose }) {
    const [links, setLinks] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open || !run?.id) return undefined
        let alive = true
        setLoading(true); setError(''); setLinks([])
        archivalApi.getRunLinks(run.id)
            .then((res) => { if (alive) setLinks(Array.isArray(res.data) ? res.data : []) })
            .catch((err) => { if (alive) setError(errorText(err, 'Failed to load download links')) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
    }, [open, run])

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Download parts</DialogTitle>
            <DialogContent>
                <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1.5 }}>
                    {run?.collectionName} · {run?.period} · run {run?.runId}. Links are presigned and expire — reopen this dialog for fresh ones.
                </Typography>
                {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
                {error && <Alert severity='error'>{error}</Alert>}
                {!loading && !error && links.length === 0 && (
                    <Typography variant='body2' sx={{ color: 'text.disabled' }}>No parts found.</Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {links.map((l) => (
                        <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Link href={l.url} target='_blank' rel='noopener noreferrer' underline='hover'
                                sx={{ fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'primary.main', '&:visited': { color: 'primary.main' } }}
                                title={l.key}>
                                {l.key}
                            </Link>
                            <CopyIcon text={l.url} title='Copy download link' />
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button size='small' variant='contained' onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}
